import type { ReactNode } from "react";
import { compact, relativeTime, usd, WINDOW_LABELS } from "@/lib/format";
import type { ResolvedLimit, Tool, ToolStats } from "@/lib/types";
import LimitBar from "./LimitBar";

interface ToolCardProps {
  tool: Tool;
  stats: ToolStats;
  now: number;
  /**
   * Slot for the manual-entry control. A prop rather than a direct import
   * because that form writes to local SQLite over an API route — the
   * blob-fed dashboard has neither, and passing nothing is how it says so.
   */
  action?: ReactNode;
  /**
   * What to say when a tool has no data at all. The local reader means "you
   * haven't used this yet"; the blob reader means "no machine has published
   * this", which is a different problem with a different fix.
   */
  emptyHint?: string;
}

/**
 * Dashboard card for a single tool: its live quota windows, plus month-to-date
 * API-equivalent value for the tools that report token usage.
 */
export default function ToolCard({ tool, stats, now, action, emptyHint }: ToolCardProps) {
  const { limits, tracksTokens, collectIssue } = stats;

  return (
    <article className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-4 transition-colors hover:border-border-strong">
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className="h-6 w-1 shrink-0 rounded-full"
            style={{ background: tool.color }}
          />
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold leading-tight">
              {tool.label}
            </h2>
            <p className="truncate text-xs text-muted">{tool.account}</p>
          </div>
        </div>
        {tool.monthlyPriceUsd > 0 ? (
          <span className="tnum shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted">
            ${tool.monthlyPriceUsd}/mo
          </span>
        ) : (
          <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted">
            Free
          </span>
        )}
      </header>

      <div className="space-y-3.5">
        {limits.length > 0 ? (
          limits.map((snap) => (
            <LimitBar
              key={`${snap.window}:${snap.label}`}
              label={snap.label || WINDOW_LABELS[snap.window] || snap.window}
              note={resetNote(snap, now)}
              used={snap.used}
              limit={snap.limit}
              unit={snap.unit}
              color={tool.color}
              inferred={snap.inferred}
            />
          ))
        ) : (
          <EmptyState stats={stats} hint={emptyHint} />
        )}
      </div>

      {collectIssue && (
        <p
          className={
            collectIssue.level === "error"
              ? "rounded-md border border-critical/30 bg-critical/10 px-2.5 py-1.5 text-[11px] text-critical"
              : "rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-[11px] text-muted"
          }
        >
          {collectIssue.level === "error" && (
            <span className="font-medium">Collect failed — </span>
          )}
          {collectIssue.message}
        </p>
      )}

      <footer className="mt-auto flex items-end justify-between gap-3 border-t border-border pt-3">
        {tracksTokens ? (
          <>
            <div>
              <p className="text-[11px] text-muted">Value this month</p>
              <p className="tnum text-lg font-semibold leading-tight">
                {usd(stats.mtdCostUsd)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-muted">Tokens, 7d</p>
              <p className="tnum text-sm leading-tight text-muted">
                {compact(stats.last7dTokens)}
              </p>
            </div>
          </>
        ) : (
          // Quota-only sources never emit token events, so a "$0.00" here would
          // claim zero usage rather than "this tool doesn't report tokens".
          <p className="text-[11px] text-faint">
            Reports quota only — no token-level cost available
          </p>
        )}
      </footer>

      {/* Wraps so the entry form gets a full-width row of its own when opened. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Sync freshness, not last activity, is what decides whether to trust
            this card: tools now report from different machines on different
            schedules, and a card fed by a sleeping laptop looks identical to a
            quiet one without it. */}
        <span className="truncate text-[11px] text-faint">
          {stats.syncedAt
            ? `Synced ${relativeTime(stats.syncedAt, now)}`
            : "Not synced yet"}
          {stats.lastEventTs &&
            ` · used ${relativeTime(stats.lastEventTs, now)}`}
        </span>
        {action}
      </div>
    </article>
  );
}

/**
 * What to say when a tool has no live quota bar. The distinction that matters
 * is whether collection is working — an idle tool and a broken collector look
 * identical otherwise.
 */
function EmptyState({ stats, hint }: { stats: ToolStats; hint?: string }) {
  if (stats.collectIssue) {
    // The issue banner below already explains it; don't say it twice.
    return <p className="text-xs text-faint">No quota data.</p>;
  }
  return (
    <p className="text-xs text-faint">
      {stats.lastEventTs
        ? "No quota window currently open — this tool reports limits only while in use."
        : (hint ??
          "Nothing recorded yet. Add a figure by hand, or use the tool and refresh.")}
    </p>
  );
}

/**
 * The note under a bar's label. An inferred bar says so first — that it was
 * assumed matters more than when it next rolls over, and a 5h window that has
 * lapsed has no next reset to report at all.
 */
function resetNote(snap: ResolvedLimit, now: number): string | undefined {
  const reset =
    snap.resetsAt === null ? null : `resets ${relativeTime(snap.resetsAt, now)}`;
  if (!snap.inferred) return reset ?? undefined;
  return reset ? `assumed reset · ${reset}` : "assumed reset · no window open";
}
